import type { Business } from "@/types/directory";

interface BusinessCredentialsProps {
  business: Business;
}

/**
 * Shows up to five certification / accreditation / collaboration logos for a
 * business. Rendered above the customer reviews block.
 *
 * This is a paid-plan feature — free listings never render the strip.
 */
export default function BusinessCredentials({ business }: BusinessCredentialsProps) {
  const credentials = (business.credentials ?? []).slice(0, 5);

  const isPaidPlan = business.plan === "Business" || business.plan === "Premium";
  if (!isPaidPlan || credentials.length === 0) return null;

  return (
    <section className="rounded-lg border border-background-200 bg-[#F5F7FA] p-6 md:p-8">
      <div className="flex flex-wrap items-center gap-2.5">
        <span className="flex h-4 w-4 items-center justify-center text-accent-600">
          <i className="ri-award-line text-base" />
        </span>
        <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
          Certifications &amp; collaborations
        </h2>
      </div>
      <p className="mt-2 text-sm text-foreground-600">
        Standards, accreditations and partner programmes {business.name} is proud to be part of.
      </p>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {credentials.map((credential) => (
          <div
            key={credential.id}
            className="flex flex-col items-center gap-2 rounded-md border border-background-200 bg-background-50 px-2.5 py-3.5 text-center transition-colors duration-200 last:col-span-2 hover:border-accent-300 hover:bg-accent-100/60 sm:last:col-span-1 sm:px-3 sm:py-4"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className={`${credential.icon} text-lg`} />
            </span>
            <span className="text-[11px] font-semibold leading-snug text-foreground-800 sm:text-xs">
              {credential.label}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}